import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from 'api/config';

import {
  INonRepeatedChallenge,
  IRepeatedChallenge,
} from 'features/challenge/config/types';
import { useUserId } from 'features/user/hooks/useUserId';

type ChallengeType = IRepeatedChallenge | INonRepeatedChallenge;

export const usePlanningBacklogQueryActions = () => {
  const queryClient = useQueryClient();
  const userId = useUserId();

  const cancelBacklogQueries = useCallback(async () => {
    await queryClient.cancelQueries({
      queryKey: [queryKeys.backlog, userId],
    });
  }, [queryClient, userId]);

  const getBacklog = useCallback(() => {
    return queryClient.getQueryData<{ items: ChallengeType[] }>([
      queryKeys.backlog,
      userId,
    ]);
  }, [queryClient, userId]);

  const updateBacklog = useCallback(
    (newItems: ChallengeType[]) => {
      queryClient.setQueryData<{ items: ChallengeType[] }>(
        [queryKeys.backlog, userId],
        (old) => ({
          ...old,
          items: newItems,
        })
      );
    },
    [queryClient, userId]
  );

  const invalidateBacklogQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.backlog, userId],
    });
  }, [queryClient, userId]);

  return {
    cancelBacklogQueries,
    getBacklog,
    updateBacklog,
    invalidateBacklogQuery,
  };
};
